import React, { useState, useMemo } from 'react';
import { usePortfolioBalances } from '@/hooks/use-portfolio-balances';
import { usePortfolioOptimizer } from '@/hooks/use-portfolio-optimizer';
import { useRebalancingExecutor } from '@/hooks/use-rebalancing-executor';
import { useWallet } from '@/contexts/WalletContext';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PortfolioOptimizerApiClient } from '@/lib/portfolio-optimizer-api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, Zap, BarChart3, Gauge, RefreshCw, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import ErrorBoundary from '@/components/ErrorBoundary';
import BacktestModal from './BacktestModal';
import RebalancingConfirmDialog from './RebalancingConfirmDialog';
import RebalancingProgressModal from './RebalancingProgressModal';
import AISignalsInfluence from './AISignalsInfluence';

type Strategy = 'MPT' | 'BLACK_LITTERMAN' | 'RISK_PARITY';

interface RebalancingRecommendation {
  token: string;
  currentWeight: number;
  targetWeight: number;
  action: 'Buy' | 'Sell';
  amount: number;
}

interface BacktestResult {
  total_return: number;
  annualized_return: number;
  volatility: number;
  sharpe_ratio: number;
  max_drawdown: number;
  win_rate: number;
  total_trades: number;
  // Add other properties as needed
}

/**
 * PortfolioOptimizer - Main component for portfolio optimization and rebalancing
 *
 * Features:
 * - Multiple optimization strategies (MPT, Black-Litterman, Risk Parity)
 * - Real-time portfolio optimization with AI signal integration
 * - Backtesting capabilities for strategy evaluation
 * - Automated rebalancing with trade execution
 * - Risk metrics and performance visualization
 *
 * @returns React component for portfolio optimization interface
 */
export default function PortfolioOptimizer() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { publicKey } = useWallet();
  const { balances, totalValueUsd } = usePortfolioBalances(publicKey);
  const { executeRebalancing, cancelRebalancing, isExecuting, isCancelling, progress } = useRebalancingExecutor();
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy>('BLACK_LITTERMAN');
  const [isBacktestModalOpen, setIsBacktestModalOpen] = useState(false);
  const [backtestResults, setBacktestResults] = useState<BacktestResult | null>(null);
  const [isBacktesting, setIsBacktesting] = useState(false);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);

  const holdings = balances.map(balance => ({
    mint: balance.mint,
    uiAmount: balance.uiAmount,
    valueUsd: balance.valueUsd,
  }));

  const { optimization, positionSizes, rebalancingTriggers, coinIdMap, signalInfluence, isOptimizing, error, optimize } = usePortfolioOptimizer(
    selectedStrategy,
    holdings
  );

  /**
   * Triggers portfolio optimization using the selected strategy
   * Calls the optimize function from the usePortfolioOptimizer hook
   */
  const handleOptimize = async () => {
    await optimize();
  };

  /**
   * Runs a backtest simulation for the selected optimization strategy
   *
   * Process:
   * 1. Validates portfolio holdings exist
   * 2. Fetches 90 days of historical price data from Supabase
   * 3. Transforms data into the required format (dates array, prices object)
   * 4. Calls the portfolio optimizer API for backtesting
   * 5. Displays results in modal or handles errors
   *
   * Data transformation logic:
   * - Groups snapshots by date and coin_id
   * - Creates sorted date array and price arrays per asset
   * - Handles missing data points by using 0 as fallback
   */
  const handleBacktest = async () => {
    if (!balances.length) {
      toast({
        title: "No Holdings",
        description: "You need portfolio holdings to run a backtest.",
        variant: "destructive",
      });
      return;
    }

    setIsBacktesting(true);
    setBacktestError(null);

    try {
      // Fetch historical price data for last 90 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 90);

      const assetIds = holdings.map(h => h.mint);

      // Fetch historical data for all assets
      const { data: historicalData, error } = await supabase
        .from('market_cap_snapshots')
        .select('coin_id, price, timestamp')
        .in('coin_id', assetIds)
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString())
        .order('timestamp', { ascending: true });

      if (error) throw error;

      // Transform data to required format for backtesting API
      // The API expects: { dates: string[], prices: Record<string, number[]> }
      const dates: string[] = [];
      const prices: Record<string, number[]> = {};

      // Group snapshots by date first, then by asset
      // This creates a matrix where each date has prices for all assets
      const dataByDate: Record<string, Record<string, number>> = {};

      historicalData?.forEach(snapshot => {
        const date = new Date(snapshot.timestamp!).toISOString().split('T')[0]; // YYYY-MM-DD format
        if (!dataByDate[date]) {
          dataByDate[date] = {};
        }
        dataByDate[date][snapshot.coin_id!] = snapshot.price;
      });

      // Convert grouped data to arrays, ensuring all assets have data for each date
      // Sort dates chronologically for proper time series
      Object.keys(dataByDate).sort().forEach(date => {
        dates.push(date);
        assetIds.forEach(assetId => {
          if (!prices[assetId]) prices[assetId] = [];
          // Use 0 for missing data points (will be filtered out in backtest)
          prices[assetId].push(dataByDate[date][assetId] || 0);
        });
      });

      const transformedData = { dates, prices };

      // Call backtest API with transformed historical data
      const portfolioOptimizerApi = new PortfolioOptimizerApiClient();
      const results = await portfolioOptimizerApi.backtestStrategy(
        selectedStrategy,
        transformedData,
        {
          initial_investment: totalValueUsd,
          rebalance_frequency: 'MONTHLY',
          transaction_costs: 0.003, // 0.3% transaction costs
        }
      );

      setBacktestResults(results);
      setIsBacktestModalOpen(true);
    } catch (error) {
      console.error('Backtest error:', {
        error,
        holdings: holdings.length,
        strategy: selectedStrategy,
        totalValue: totalValueUsd,
        timestamp: new Date().toISOString()
      });
      const errorMessage = error instanceof Error ? error.message : 'Failed to run backtest';
      let enhancedMessage = errorMessage;

      // Enhance error message with data requirements for better UX
      if (errorMessage.includes('data') || errorMessage.includes('historical')) {
        enhancedMessage = `${errorMessage}\n\nData Requirements:\n• At least 30 days of historical price data\n• Valid price data for all portfolio assets\n• Continuous trading data without gaps`;
      } else if (errorMessage.includes('constraint') || errorMessage.includes('invalid')) {
        enhancedMessage = `${errorMessage}\n\nCommon Issues:\n• Insufficient historical data\n• Invalid asset prices\n• Strategy parameters out of bounds`;
      }

      setBacktestError(enhancedMessage);
      toast({
        title: "Backtest Failed",
        description: enhancedMessage,
        variant: "destructive",
      });
    } finally {
      setIsBacktesting(false);
    }
  };

  /**
   * Initiates the rebalancing confirmation dialog
   * Shows user the trades that will be executed before proceeding
   */
  const handleExecuteRebalancing = () => {
    setShowConfirmDialog(true);
  };

  /**
   * Executes the rebalancing trades after user confirmation
   *
   * Process:
   * 1. Closes confirmation dialog and shows progress modal
   * 2. Calls executeRebalancing with trade recommendations
   * 3. Updates portfolio data cache on success
   * 4. Shows success/failure notifications
   * 5. Handles partial failures gracefully
   */
  const handleConfirmRebalancing = async () => {
    setShowConfirmDialog(false);
    setShowProgressModal(true);

    try {
      const result = await executeRebalancing(rebalancingRecommendations);

      // Invalidate and refetch portfolio balances to reflect changes
      queryClient.invalidateQueries({ queryKey: ['portfolio-balances'] });

      // Show detailed results with success/failure breakdown
      if (result.success) {
        toast({
          title: "Rebalancing Complete",
          description: `Successfully executed ${result.executed} trades`,
        });
      } else {
        console.error('Rebalancing partially failed:', {
          result,
          recommendations: rebalancingRecommendations,
          timestamp: new Date().toISOString()
        });
        toast({
          title: "Rebalancing Partially Failed",
          description: `${result.executed} successful, ${result.failed} failed. Check console for details.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Rebalancing execution failed:', {
        error,
        recommendations: rebalancingRecommendations,
        timestamp: new Date().toISOString()
      });
      const errorMessage = error instanceof Error ? error.message : 'Unknown rebalancing error';
      toast({
        title: "Rebalancing Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  /**
   * Cancels the ongoing rebalancing process
   * Calls the cancelRebalancing function from the hook
   */
  const handleCancelRebalancing = () => {
    cancelRebalancing();
  };

  // Calculate current allocation for comparison
  const currentAllocation = useMemo(() => {
    if (!balances || totalValueUsd === 0) return [];

    return balances.map(balance => ({
      name: balance.ticker || balance.mint.slice(0, 4),
      current: (balance.valueUsd / totalValueUsd) * 100,
      optimal: optimization?.optimal_weights[balance.mint] ? optimization.optimal_weights[balance.mint] * 100 : 0,
    }));
  }, [balances, totalValueUsd, optimization]);

  // Prepare rebalancing recommendations from optimization triggers
  // This transforms raw trigger data into user-friendly trade recommendations
  const rebalancingRecommendations: RebalancingRecommendation[] = useMemo(() => {
    if (!rebalancingTriggers || !balances || !coinIdMap) return [];

    return rebalancingTriggers.map(trigger => {
      const balance = balances.find(b => b.mint === trigger.asset);
      const coinId = coinIdMap[trigger.asset];
      const currentWeight = trigger.current_weight * 100; // Convert to percentage
      const targetWeight = trigger.optimal_weight * 100; // Convert to percentage
      const deviation = targetWeight - currentWeight; // Positive = buy, negative = sell
      const action: 'Buy' | 'Sell' = deviation > 0 ? 'Buy' : 'Sell';
      // Calculate dollar amount: |deviation|% of total portfolio value
      const amount = Math.abs(deviation) * totalValueUsd / 100;

      return {
        coinId: coinId || trigger.asset, // Use coinId for trading, fallback to asset mint
        token: balance?.ticker || trigger.asset.slice(0, 8), // Display name with fallback
        currentWeight,
        targetWeight,
        action,
        amount,
      };
    });
  }, [rebalancingTriggers, balances, totalValueUsd, coinIdMap]);

  // Risk metrics (mock data for now)
  const riskMetrics = {
    var: optimization?.expected_volatility ? optimization.expected_volatility * 2 : 0.05, // VaR approximation
    beta: 0.8, // Mock beta
    riskScore: Math.min(100, Math.max(0, (optimization?.expected_volatility || 0.2) * 500)), // 0-100 scale
  };


  return (
    <ErrorBoundary>
      <div className="space-y-6">
        {/* Strategy Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Optimization Strategy</CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup value={selectedStrategy} onValueChange={(value) => setSelectedStrategy(value as Strategy)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="MPT" id="mpt" />
                <Label htmlFor="mpt">Modern Portfolio Theory (MPT)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="BLACK_LITTERMAN" id="black-litterman" />
                <Label htmlFor="black-litterman">Black-Litterman Model</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="RISK_PARITY" id="risk-parity" />
                <Label htmlFor="risk-parity">Risk Parity</Label>
              </div>
            </RadioGroup>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleOptimize} disabled={isOptimizing || !balances.length}>
                <Zap className="w-4 h-4 mr-2" />
                {isOptimizing ? 'Optimizing...' : 'Run Optimization'}
              </Button>
              <Button onClick={handleBacktest} variant="outline" disabled={isBacktesting || !balances.length}>
                <BarChart3 className="w-4 h-4 mr-2" />
                {isBacktesting ? 'Running Backtest...' : 'Backtest Strategy'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Empty State - No Holdings */}
        {!balances.length && (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                  <Plus className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No Portfolio Holdings</h3>
                <p className="text-muted-foreground max-w-sm mb-4">
                  Add tokens to your portfolio to use the optimizer
                </p>
                <Button variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tokens
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Loading State */}
      {isOptimizing && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <div role="status" aria-live="polite">
                <p>Optimizing portfolio... This may take 5-10 seconds.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Error State */}
      {error && (
        <Alert variant="destructive" role="alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <div>
              <strong>Optimization Failed</strong>
              <p className="text-sm mt-1">{error.message}</p>
              {error.message.includes('constraint') && (
                <div className="mt-2 text-sm">
                  <p><strong>Possible constraint violations:</strong></p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Maximum allocation per asset (30%) exceeded</li>
                    <li>Minimum allocation per asset (1%) not met</li>
                    <li>Risk tolerance limits exceeded</li>
                    <li>Insufficient historical data for covariance calculation</li>
                  </ul>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  console.log('Retrying optimization after error:', error);
                  handleOptimize();
                }}
                disabled={isOptimizing}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Retry Optimization
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Optimization Results */}
      {(optimization || isOptimizing) && (
        <>
          {/* Results Card */}
          <Card>
            <CardHeader>
              <CardTitle>Optimization Results</CardTitle>
            </CardHeader>
            <CardContent>
              {isOptimizing ? (
                // Skeleton loaders while optimizing
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="text-center space-y-2">
                      <Skeleton className="h-4 w-24 mx-auto" />
                      <Skeleton className="h-8 w-16 mx-auto" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Expected Return</p>
                    <p className="text-2xl font-bold text-success">
                      {(optimization.expected_return * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Volatility</p>
                    <p className="text-2xl font-bold text-warning">
                      {(optimization.expected_volatility * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Sharpe Ratio</p>
                    <p className="text-2xl font-bold">
                      {optimization.sharpe_ratio.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Optimization Time</p>
                    <p className="text-2xl font-bold">
                      {optimization.optimization_time.toFixed(2)}s
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {isOptimizing ? (
                  // Skeleton loaders for charts
                  <>
                    <div>
                      <Skeleton className="h-6 w-32 mb-4" />
                      <Skeleton className="h-80 w-full" />
                    </div>
                    <div>
                      <Skeleton className="h-6 w-40 mb-4" />
                      <Skeleton className="h-80 w-full" />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Optimal Allocation Pie Chart */}
                    <div>
                      <h4 className="font-medium mb-4">Optimal Allocation</h4>
                      <div className="h-[200px] sm:h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(optimization.optimal_weights).map(([asset, weight], index) => ({
                              name: asset.slice(0, 4) + '...',
                              value: weight * 100,
                              fill: ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'][index % 5],
                            }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {Object.entries(optimization.optimal_weights).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'][index % 5]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Current vs Optimal Comparison */}
                    <div>
                      <h4 className="font-medium mb-4">Current vs Optimal Allocation</h4>
                      <div className="h-[200px] sm:h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={currentAllocation}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                          <Legend />
                          <Bar dataKey="current" fill="#8884d8" name="Current" />
                          <Bar dataKey="optimal" fill="#82ca9d" name="Optimal" />
                        </BarChart>
                      </ResponsiveContainer>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rebalancing Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle>Rebalancing Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              {isOptimizing ? (
                // Skeleton loaders for rebalancing table
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b">
                          <Skeleton className="h-4 w-16" />
                          <Skeleton className="h-4 w-12" />
                          <Skeleton className="h-4 w-12" />
                          <Skeleton className="h-6 w-12" />
                          <Skeleton className="h-4 w-16" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : rebalancingRecommendations.length === 0 ? (
                // Empty state - no rebalancing needed
                <div className="text-center py-8">
                  <Gauge className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Portfolio is Already Optimal</h3>
                  <p className="text-muted-foreground">
                    Portfolio is already optimally balanced - no rebalancing needed
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Token</th>
                          <th className="text-right py-2">Current Weight</th>
                          <th className="text-right py-2">Target Weight</th>
                          <th className="text-center py-2">Action</th>
                          <th className="text-right py-2">Amount ($)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rebalancingRecommendations.map((rec, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2">{rec.token}</td>
                            <td className="text-right py-2">{rec.currentWeight.toFixed(1)}%</td>
                            <td className="text-right py-2">{rec.targetWeight.toFixed(1)}%</td>
                            <td className="text-center py-2">
                              <span className={cn(
                                "px-2 py-1 rounded text-xs font-medium",
                                rec.action === 'Buy' ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                              )}>
                                {rec.action}
                              </span>
                            </td>
                            <td className="text-right py-2">${rec.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4">
                    <Button onClick={handleExecuteRebalancing} className="w-full">
                      Execute Rebalancing
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Risk Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Risk Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              {isOptimizing ? (
                // Skeleton loaders for risk metrics
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="text-center space-y-2">
                      <Skeleton className="h-4 w-20 mx-auto" />
                      <Skeleton className="h-8 w-16 mx-auto" />
                    </div>
                  ))}
                </div>
              ) : !optimization ? (
                // Empty state - no optimization results
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Unable to Optimize Portfolio</h3>
                  <p className="text-muted-foreground">
                    Unable to optimize portfolio with current constraints - try adjusting strategy or holdings
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">VaR (95%)</p>
                    <p className="text-2xl font-bold text-destructive">
                      {(riskMetrics.var * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Beta</p>
                    <p className="text-2xl font-bold">
                      {riskMetrics.beta.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-center">
                    <RiskGauge value={riskMetrics.riskScore} />
                    <p className="text-sm text-muted-foreground mt-2">Risk Score</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Signals Integration (for Black-Litterman) */}
          {selectedStrategy === 'BLACK_LITTERMAN' && (
            <AISignalsInfluence
              assets={holdings.map(h => h.mint)}
              signalInfluence={signalInfluence}
            />
          )}
        </>
      )}

      <BacktestModal
        isOpen={isBacktestModalOpen}
        onClose={() => setIsBacktestModalOpen(false)}
        strategy={selectedStrategy}
        holdings={holdings}
        backtestResults={backtestResults}
        isLoading={isBacktesting}
        error={backtestError}
      />

      <RebalancingConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmRebalancing}
        recommendations={rebalancingRecommendations}
        isExecuting={isExecuting}
      />

      <RebalancingProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        onCancel={handleCancelRebalancing}
        progress={progress}
        isExecuting={isExecuting}
        isCancelling={isCancelling}
      />
    </div>
    </ErrorBoundary>
  );
}

/**
 * Simple circular gauge component for displaying risk scores
 *
 * @param value - Risk score value (0-100)
 * @returns SVG-based circular gauge with color-coded risk levels
 */
function RiskGauge({ value }: { value: number }) {
  const percentage = Math.min(100, Math.max(0, value)); // Clamp to 0-100 range
  // Color coding: green (<40), yellow (40-70), red (>70)
  const color = percentage > 70 ? '#ef4444' : percentage > 40 ? '#f59e0b' : '#10b981';

  return (
    <div className="relative w-16 h-16 mx-auto">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
        {/* Background circle */}
        <path
          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="2"
        />
        {/* Progress arc based on percentage */}
        <path
          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={`${percentage}, 100`} // Show progress as percentage of circle
        />
      </svg>
      {/* Centered percentage text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ color }}>
          {percentage.toFixed(0)}
        </span>
      </div>
    </div>
  );
}